/**
 * RenderServers — beheer + goedkeuring van render-server devices (Macs).
 *
 * Een machine meldt zich automatisch aan (supervisor) en verschijnt hier als
 * 'pending'. Een admin keurt 'm eenmalig goed; pas dan krijgt het toegang tot
 * de job-library. Per device zie je status, online/offline, capabilities,
 * waartoe het toegang heeft (job-categorieën), bundle-versie en laatste contact.
 * Acties: goedkeuren / intrekken / scopes bewerken / nu updaten / logs bekijken.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControlLabel, Checkbox, Tooltip,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Block as RevokeIcon, Refresh as RefreshIcon,
  SystemUpdateAlt as UpdateIcon, OpenInNew as LogsIcon, Tune as ScopeIcon,
  Download as DownloadIcon, DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { bevestig } from '../../../utils/bevestig';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, SectionHeader } from '../adminSettingsCommon';

const API = `${BACKEND_URL}/Api/render-servers`;

const ALL_CATEGORIES = [
  { key: 'name-title', label: 'Naamtitels' },
  { key: 'narrowcasting', label: 'Narrowcasting' },
  { key: 'srt', label: 'Ondertitels' },
  { key: 'qa', label: 'Video-QA' },
  { key: 'ritmo', label: 'Ritmo' },
];

const STATUS_STYLE = {
  pending: { color: '#b45309', bg: '#fef3c7', label: 'Wacht op goedkeuring' },
  approved: { color: '#10b981', bg: '#ecfdf5', label: 'Goedgekeurd' },
  revoked: { color: '#ef4444', bg: '#fee2e2', label: 'Ingetrokken' },
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

function navigateToLogs(workerId) {
  // Switch to the logging tab, render-server jobs, pre-filtered on this machine.
  // Normaliseer naar kale machinenaam (strip ".local") zodat het matcht met de
  // worker_id waarmee jobs worden opgeslagen.
  const wid = String(workerId || '').split('/')[0].replace(/\.local$/i, '');
  const sp = new URLSearchParams(window.location.search);
  sp.set('tab', 'logging'); sp.set('section', 'render-logs');
  if (wid) sp.set('worker_id', wid); else sp.delete('worker_id');
  window.history.replaceState({}, '', `${window.location.pathname}?${sp.toString()}`);
  window.dispatchEvent(new CustomEvent('ff-admin-navigate', {
    detail: { tab: 'logging', section: 'render-logs' },
  }));
}

export default function RenderServers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});         // device_id → action
  const [scopeDevice, setScopeDevice] = useState(null);
  const [scopeSel, setScopeSel] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // De server tarde hier vroeger zijn eigen werkkopie van de render-worker. Dat
  // gaf iets anders dan wat de Macs draaien en het was niet ondertekend. Nu
  // wijst hij naar dezelfde gepubliceerde bundle, met versie en sha256 erbij.
  const downloadInstaller = async () => {
    setDownloading(true);
    try {
      const res = await authFetch(`${API}/installer`);
      if (!res.ok) throw new Error();
      const { download_url: url, version } = await res.json();
      if (!url) throw new Error();
      const a = document.createElement('a');
      a.href = url;
      a.download = `ff-render-worker-${version || 'installer'}.tgz`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Render-worker ${version} wordt gedownload`);
    } catch {
      toast.error('Installer downloaden mislukt');
    } finally {
      setDownloading(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(API);
      if (!res.ok) throw new Error();
      const data = await res.json();
      // pending first, then approved, then revoked; within group most-recent first
      const rank = { pending: 0, approved: 1, revoked: 2 };
      data.sort((a, b) => (rank[a.status] - rank[b.status]) ||
        (new Date(b.last_seen_at || 0) - new Date(a.last_seen_at || 0)));
      setRows(data);
    } catch {
      toast.error('Render servers laden mislukt');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const action = async (device, kind) => {
    setBusy(prev => ({ ...prev, [device.device_id]: kind }));
    const paths = {
      approve: `${API}/${device.device_id}/approve`,
      revoke: `${API}/${device.device_id}/revoke`,
      update: `${API}/${device.device_id}/request-update`,
    };
    const labels = { approve: 'Goedgekeurd', revoke: 'Ingetrokken', update: 'Update aangevraagd' };
    try {
      const res = await authFetch(paths[kind], { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      toast.success(labels[kind]);
      fetchData();
    } catch (err) {
      toast.error(`Mislukt: ${err.message}`);
    } finally {
      setBusy(prev => ({ ...prev, [device.device_id]: null }));
    }
  };

  const removeDevice = async (device) => {
    if (!(await bevestig({ titel: `Device "${device.hostname}" verwijderen?`, tekst: `Omgeving: ${device.environment || '?'}. Het device verdwijnt definitief uit de lijst.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    setBusy(prev => ({ ...prev, [device.device_id]: 'delete' }));
    try {
      const res = await authFetch(`${API}/${device.device_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      toast.success('Device verwijderd');
      fetchData();
    } catch (err) {
      toast.error(`Verwijderen mislukt: ${err.message}`);
    } finally {
      setBusy(prev => ({ ...prev, [device.device_id]: null }));
    }
  };

  const openScopes = (device) => {
    setScopeDevice(device);
    setScopeSel(device.allowed_job_categories ?? ALL_CATEGORIES.map(c => c.key));
  };
  const saveScopes = async () => {
    const device = scopeDevice;
    setScopeDevice(null);
    try {
      // null = alle categorieën
      const all = scopeSel.length === ALL_CATEGORIES.length;
      const res = await authFetch(`${API}/${device.device_id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowed_job_categories: all ? null : scopeSel }),
      });
      if (!res.ok) throw new Error();
      toast.success('Toegang bijgewerkt');
      fetchData();
    } catch {
      toast.error('Toegang bijwerken mislukt');
    }
  };

  const catLabels = (cats) => (cats == null
    ? 'Alle job-types'
    : (cats.length ? cats.map(c => ALL_CATEGORIES.find(x => x.key === c)?.label || c).join(', ') : 'Geen'));

  return (
    <Box>
      <SectionHeader
        title="Render servers"
        subtitle="Beheer en goedkeuring van render-server devices. Nieuwe machines verschijnen automatisch en wachten op goedkeuring voordat ze jobs kunnen oppakken."
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={downloadInstaller} disabled={downloading}
            startIcon={downloading ? <CircularProgress size={14} /> : <DownloadIcon />}
            size="small" variant="outlined" sx={{ textTransform: 'none' }}>
            Installer downloaden
          </Button>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline }}>
            Alleen voor <b>Apple Silicon</b> Macs (M1 of nieuwer) · uitpakken → <code>install.command</code>
          </Typography>
        </Box>
        <Button onClick={fetchData} startIcon={<RefreshIcon />} size="small" sx={{ textTransform: 'none' }}>Vernieuwen</Button>
      </Box>

      {loading ? (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress size={22} sx={{ color: T.outlineVariant }} /></Box>
      ) : rows.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center', bgcolor: T.surface, borderRadius: '0.6rem', border: `1px solid ${T.surfaceHigh}` }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.9rem', color: T.outline }}>
            Nog geen render-servers aangemeld. Download hierboven de installer, pak 'm uit op een Apple Silicon Mac en draai <code>install.command</code> om er één toe te voegen.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {rows.map(d => {
            const ss = STATUS_STYLE[d.status] || { color: T.outline, bg: T.surfaceLow, label: d.status };
            const b = busy[d.device_id];
            return (
              <Box key={d.device_id} sx={{
                bgcolor: T.surface, borderRadius: '0.7rem', border: `1px solid ${d.status === 'pending' ? '#fcd34d' : T.surfaceHigh}`,
                p: 2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2,
                alignItems: { md: 'center' }, justifyContent: 'space-between',
              }}>
                {/* Identity + meta */}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography sx={{ fontFamily: T.font, fontSize: '1rem', fontWeight: 800, color: T.text }}>{d.hostname || d.device_id}</Typography>
                    <Chip label={ss.label} size="small" sx={{ bgcolor: ss.bg, color: ss.color, fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 700, height: 22 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.online ? '#10b981' : T.outlineVariant }} />
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline }}>{d.online ? 'online' : `laatst ${fmtDate(d.last_seen_at)}`}</Typography>
                    </Box>
                    {d.environment && <Chip label={d.environment} size="small" sx={{ bgcolor: T.surfaceLow, color: T.secondary, fontFamily: T.fontBody, fontSize: '0.65rem', height: 20 }} />}
                  </Box>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline }}>
                    Toegang: <b style={{ color: T.secondary }}>{catLabels(d.allowed_job_categories)}</b>
                    {d.app_git_sha && <> · versie <code>{d.app_git_sha.slice(0, 7)}</code></>}
                    {d.current_job && <> · bezig: {d.current_job}</>}
                  </Typography>
                  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant, mt: 0.25 }}>
                    {d.os_version || '—'}
                    {d.capabilities?.ae?.length ? ` · AE ${d.capabilities.ae.join(', ')}` : ''}
                    {d.capabilities?.ffmpeg ? ' · ffmpeg' : ''}
                    {d.last_ip ? ` · ${d.last_ip}` : ''}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  {d.status === 'pending' && (
                    <Button size="small" variant="contained" disableElevation onClick={() => action(d, 'approve')} disabled={!!b}
                      startIcon={b === 'approve' ? <CircularProgress size={14} /> : <ApproveIcon />}
                      sx={{ textTransform: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>Goedkeuren</Button>
                  )}
                  {d.status === 'approved' && (
                    <>
                      <Tooltip title="Forceer nu: nieuwste gesigneerde bundle downloaden + verifiëren + herstart"><span>
                        <Button size="small" variant="outlined" onClick={() => action(d, 'update')} disabled={!!b}
                          startIcon={b === 'update' ? <CircularProgress size={14} /> : <UpdateIcon />} sx={{ textTransform: 'none' }}>Nu updaten</Button>
                      </span></Tooltip>
                      <Button size="small" variant="text" onClick={() => openScopes(d)} startIcon={<ScopeIcon />} sx={{ textTransform: 'none', color: T.secondary }}>Toegang</Button>
                    </>
                  )}
                  {d.status === 'revoked' && (
                    <>
                      <Button size="small" variant="contained" disableElevation onClick={() => action(d, 'approve')} disabled={!!b}
                        startIcon={b === 'approve' ? <CircularProgress size={14} /> : <ApproveIcon />}
                        sx={{ textTransform: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>Heractiveren</Button>
                      <Button size="small" variant="text" onClick={() => removeDevice(d)} disabled={!!b}
                        startIcon={b === 'delete' ? <CircularProgress size={14} /> : <DeleteIcon />}
                        sx={{ textTransform: 'none', color: '#ef4444' }}>Verwijderen</Button>
                    </>
                  )}
                  {d.status !== 'revoked' && (
                    <Button size="small" variant="text" onClick={() => action(d, 'revoke')} disabled={!!b}
                      startIcon={b === 'revoke' ? <CircularProgress size={14} /> : <RevokeIcon />} sx={{ textTransform: 'none', color: '#ef4444' }}>Intrekken</Button>
                  )}
                  <Button size="small" variant="text" onClick={() => navigateToLogs(d.hostname)} startIcon={<LogsIcon />} sx={{ textTransform: 'none', color: T.secondary }}>Logs</Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Scope-edit dialog */}
      <Dialog open={!!scopeDevice} onClose={() => setScopeDevice(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>Toegang — {scopeDevice?.hostname}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline, mb: 1 }}>
            Welke job-types mag dit apparaat oppakken?
          </Typography>
          {ALL_CATEGORIES.map(c => (
            <FormControlLabel key={c.key} control={
              <Checkbox checked={scopeSel.includes(c.key)} onChange={(e) => {
                setScopeSel(prev => e.target.checked ? [...prev, c.key] : prev.filter(x => x !== c.key));
              }} />
            } label={c.label} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScopeDevice(null)} sx={{ textTransform: 'none' }}>Annuleren</Button>
          <Button onClick={saveScopes} variant="contained" disableElevation sx={{ textTransform: 'none' }}>Opslaan</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
